// Pure helpers for reading the profit & loss statement's section subtotals.
// Account sections are Income, Cost of goods sold, Expenses, Other income,
// and Other expenses. Gross profit and net operating income are calculated
// lines and are not added again.
import type { FinanceStatementResponse } from "@/lib/api";

type PnlSectionKind = "revenue" | "expense";

/** Calculated statement lines. Their labels already are the amount. */
export function isCalculatedStatementLine(label: string): boolean {
  const text = label.toLowerCase();
  return text === "gross profit" || text === "net operating income";
}

/**
 * Maps a P&L section label to a meaning. "Cost of goods sold" belongs with
 * expenses, so a cost/expense word wins over a revenue/income word.
 */
function classifyPnlSection(label: string): PnlSectionKind | null {
  if (isCalculatedStatementLine(label)) return null;
  const text = label.toLowerCase();
  if (
    text.includes("expense") ||
    text.includes("cost") ||
    text.includes("expenditure")
  ) {
    return "expense";
  }
  if (
    text.includes("revenue") ||
    text.includes("income") ||
    text.includes("sales")
  ) {
    return "revenue";
  }
  return null;
}

/**
 * Sum of the section subtotals for one kind, or `null` when no such section
 * is present (so callers show "—" instead of a fake zero). Scalar
 * `subtotal_cents` is preferred; comparative `subtotals_cents[0]` is the
 * fallback.
 */
export function pnlSectionSubtotal(
  statement: FinanceStatementResponse,
  kind: PnlSectionKind,
): number | null {
  let total = 0;
  let found = false;
  for (const section of statement.sections ?? []) {
    if (classifyPnlSection(section.label) !== kind) continue;
    const subtotal = section.subtotal_cents ?? section.subtotals_cents?.[0];
    if (subtotal === null || subtotal === undefined) continue;
    found = true;
    total += subtotal;
  }
  return found ? total : null;
}

/** Net income from the section subtotals, falling back to the grand total. */
export function pnlNetIncome(
  statement: FinanceStatementResponse,
): number | null {
  const revenue = pnlSectionSubtotal(statement, "revenue");
  const expenses = pnlSectionSubtotal(statement, "expense");
  if (revenue !== null && expenses !== null) return revenue - expenses;
  return statement.grand_total_cents ?? statement.grand_totals_cents?.[0] ?? null;
}

/**
 * The headline P&L numbers for one comparative column, taken from the section
 * subtotals. Column 0 is the report period, column 1 the comparison.
 */
interface PnlHeadline {
  revenueCents: number | null;
  expensesCents: number | null;
  netIncomeCents: number | null;
}

export function pnlHeadline(
  statement: FinanceStatementResponse,
  column = 0,
): PnlHeadline {
  const sectionValue = (kind: PnlSectionKind): number | null => {
    let total = 0;
    let found = false;
    for (const section of statement.sections ?? []) {
      if (classifyPnlSection(section.label) !== kind) continue;
      const value =
        section.subtotals_cents?.[column] ?? section.subtotal_cents ?? null;
      if (value === null || value === undefined) continue;
      found = true;
      total += value;
    }
    return found ? total : null;
  };
  const revenueCents = sectionValue("revenue");
  const expensesCents = sectionValue("expense");
  const netIncomeCents =
    revenueCents !== null && expensesCents !== null
      ? revenueCents - expensesCents
      : (statement.grand_totals_cents?.[column] ??
        statement.grand_total_cents ??
        null);
  return { revenueCents, expensesCents, netIncomeCents };
}
